import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CausalPeriod, { useCausalPeriod } from './CausalPeriod';
import { causalApi as api } from './causalApi';
import ClusterChart, { clusterColor as color } from './ClusterChart';
import './CausalDashboard.scss';
import { readClusterCache as read, saveClusterCache as save } from './clusterCache';

export default function CauseClusters() {
  const period = useCausalPeriod(true, 'cause-clusters-period');
  const [n, setN] = useState(() => read('cause-clusters-n') || 5);
  const [mode, setMode] = useState(() => read('cause-clusters-view') || 'donut');
  const [key, setKey] = useState('');
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cluster, setCluster] = useState(null);
  const [selected, setSelected] = useState(null);
  const sidebar = useRef(null);
  useEffect(() => {
    if (cluster != null && window.innerWidth <= 1100) sidebar.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [cluster]);
  useEffect(() => { save('cause-clusters-n', n); }, [n]);
  useEffect(() => { save('cause-clusters-view', mode); }, [mode]);
  const storageKey = `cause-clusters:${period.query}:${n}`;
  const currentKey = useRef(storageKey);
  currentKey.current = storageKey;
  useEffect(() => {
    const cached = read(storageKey);
    setLoading(false);
    setKey(cached?.id || ''); setJob(cached?.result ? cached : null); setHistory([]); setError(''); setSelected(null); setCluster(null);
    if (!period.ready || !Number.isInteger(Number(n)) || Number(n) < 1 || Number(n) > 30) return;
    let live = true;
    setLoading(true);
    api(`clusters?${period.query}&n=${Number(n)}`).then(rows => {
      if (!live) return;
      setHistory(rows);
      const chosen = rows.find(row => row.id === cached?.id) || rows.find(row => row.status === 'processing' || row.status === 'pending') || rows.find(row => row.status === 'completed') || rows[0];
      if (chosen && chosen.id !== cached?.id) save(storageKey, { id: chosen.id });
      setKey(chosen?.id || '');
      if (!chosen || chosen.id !== cached?.id) { setJob(null); if (!chosen) save(storageKey, null); }
    }).catch(err => { if (live) setError(err.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [storageKey, period.ready]);
  useEffect(() => {
    setEvents([]);
    if (!period.ready) return;
    let live = true;
    api(`cluster-events?${period.query}`).then(rows => { if (live) setEvents(rows); }).catch(err => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [period.ready, period.query]);
  useEffect(() => {
    if (!key || read(storageKey)?.id !== key) return;
    let live = true;
    let timer;
    const requestedScope = storageKey;
    const load = async () => {
      try {
        const value = await api(`clusters/${encodeURIComponent(key)}`);
        if (!live || currentKey.current !== requestedScope) return;
        save(requestedScope, value);
        setJob(value);
        setHistory(rows => rows.map(row => row.id === value.id ? { ...row, status: value.status } : row));
        if (value.result?.events) setEvents(value.result.events);
        if (value.status === 'failed') setError(value.error || 'Ошибка кластеризации');
        if (value.status === 'pending' || value.status === 'processing') timer = setTimeout(load, 3000);
      } catch (err) { if (live) setError(err.message); }
    };
    load();
    return () => { live = false; clearTimeout(timer); };
  }, [key, storageKey]);
  const start = async (force = false) => {
    const requestedKey = storageKey;
    setSubmitting(true); setError(''); setCluster(null); setSelected(null);
    try {
      const value = await api('clusters', { start_date: period.start, end_date: period.end, group_id: period.group || null, n: Number(n), force });
      if (currentKey.current !== requestedKey) return;
      // A reused result can have the same ID: keep its already loaded map.
      setJob(old => old?.id === value.id ? old : value); setKey(value.id);
      if (read(requestedKey)?.id !== value.id) save(requestedKey, value);
      const rows = await api(`clusters?${period.query}&n=${Number(n)}`);
      if (currentKey.current === requestedKey) setHistory(rows);
    } catch (err) { if (currentKey.current === requestedKey) setError(err.message); }
    finally { setSubmitting(false); }
  };
  const result = job?.result;
  const running = submitting || job?.status === 'pending' || job?.status === 'processing';
  const points = result?.points || [];
  const groups = useMemo(() => (result?.clusters || []).map(row => {
    const members = (result?.points || []).filter(point => point.cluster_id === row.id);
    return { ...row, messages: members.reduce((sum, point) => sum + point.messages, 0), causes: members.length };
  }).sort((a, b) => b.messages - a.messages), [result]);
  const total = groups.reduce((sum, row) => sum + row.messages, 0);
  const active = groups.find(row => row.id === cluster);
  const members = points.filter(point => point.cluster_id === cluster).sort((a, b) => b.messages - a.messages);
  const openCluster = id => { setCluster(id); setSelected(null); };
  const messageLink = point => `/emotive-analysis?tab=llm-causes&${new URLSearchParams({ group_id: job.group_id || '', start_date: job.start_date, end_date: job.end_date, cause_id: point.cause_id })}`;
  useEffect(() => {
    const close = event => { if (event.key === 'Escape') { setCluster(null); setSelected(null); } };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);
  return <section className="causal-dashboard causal-clusters">
    <header className="causal-heading"><div><h2>Распределение тематик</h2><span>Смысловые кластеры причин эмоций</span></div></header>
    <CausalPeriod period={period}><label>Кластеров N<input className="form-control form-control-sm" type="number" min="1" max="30" value={n} onChange={e => setN(e.target.value)} /></label>
      <button className="btn btn-sm btn-primary" disabled={running || loading || !period.ready || !Number.isInteger(Number(n)) || Number(n) < 1 || Number(n) > 30} onClick={() => start()}>{running ? 'Пересчёт…' : 'Получить результат'}</button>
      <button className="btn btn-sm btn-outline-secondary" disabled={running || loading || !period.ready || !Number.isInteger(Number(n)) || Number(n) < 1 || Number(n) > 30} onClick={() => start(true)}>Пересчитать заново</button></CausalPeriod>
    {history.length > 0 && <label className="d-block mb-3">Сохранённые расчёты<select className="form-select form-select-sm" value={key} onChange={e => { setKey(e.target.value); save(storageKey, { id: e.target.value }); setJob(null); setError(''); setSelected(null); setCluster(null); }}>
      {history.map(row => <option key={row.id} value={row.id}>{new Date(row.created_at).toLocaleString('ru-RU')} · {{ completed: 'Готово', pending: 'В очереди', processing: 'В работе', failed: 'Ошибка' }[row.status] || row.status} · {row.total} причин</option>)}
    </select></label>}
    {job?.stale && <p className="alert alert-warning py-2">Данные или справочники изменились после этого расчёта. Нажмите «Пересчитать заново», чтобы обновить распределение.</p>}
    {(error || period.error) && <p role="alert" className="alert alert-danger py-2">{error || period.error}</p>}
    {running && <div className="causal-panel mb-3" role="status">{job?.status === 'pending' ? 'В очереди модели' : 'LLM формирует группы и распределяет причины'} · {job?.done || 0} / {job?.total || '…'} причин</div>}
    <div className="causal-map-layout"><div className="causal-panel">
      {!result && <p className="text-muted p-4">{loading ? 'Загрузка сохранённых расчётов…' : 'Выберите сохранённый расчёт или получите результат. Каждая точка — причина эмоции, цвет — её кластер.'}</p>}
      {result && <>
        <div className="cluster-chart-toolbar"><div><strong>{points.length}</strong> причин <span>·</span> <strong>{groups.length}</strong> кластеров</div>
          <div className="cluster-view-toggle" role="group" aria-label="Вид распределения"><button aria-pressed={mode === 'donut'} onClick={() => setMode('donut')}>Доли упоминаний</button><button aria-pressed={mode === 'map'} onClick={() => setMode('map')}>Векторное пространство</button></div>
        </div>
        <ClusterChart result={result} groups={groups} mode={mode} active={cluster} onSelect={openCluster} onPoint={point => { setCluster(point.cluster_id); setSelected(point); }} />
        <div className="cluster-summary-list">{groups.map(row => <button key={row.id} className={cluster === row.id ? 'active' : ''} onClick={() => openCluster(row.id)}>
          <i style={{ background: color(row.id) }}>{row.id}</i><span>{row.name}<small>{row.causes} причин</small></span><b>{total ? (row.messages / total * 100).toFixed(1) : 0}%<small>{row.messages.toLocaleString('ru-RU')} упоминаний</small></b>
          <em style={{ background: color(row.id), width: `${total ? row.messages / total * 100 : 0}%` }} />
        </button>)}</div>
        <p className="cluster-footnote">{mode === 'map' ? 'Области построены вокруг центров кластеров; принадлежность причины задаёт цвет точки. Координаты — оценка LLM, размер точки — число упоминаний.' : 'Доли рассчитаны по сумме упоминаний причин. Одно сообщение может содержать несколько причин.'}</p>
      </>}
    </div><aside className="causal-panel cluster-sidebar" ref={sidebar}>
      <h3>События и проблемы<span>Топ 20</span></h3><div className="causal-rank cluster-events">{events.slice(0, 20).map(row => <Link key={row.id} to={`/emotive-analysis?tab=llm-causes&${period.query}&event_id=${row.id}`}><span>{row.name}</span><b>{row.messages}</b></Link>)}{!events.length && <p className="text-muted">Нет упоминаний за период.</p>}</div>
      {active && <div className="cluster-detail-panel" role="region" aria-label={`Кластер: ${active.name}`}>
        <div className="cluster-detail-header"><span style={{ background: color(active.id) }}>Кластер {active.id}</span><button className="btn btn-sm btn-light" onClick={() => { setCluster(null); setSelected(null); }} aria-label="Закрыть состав кластера">✕</button></div>
        <h3>{active.name}</h3><div className="cluster-detail-stats"><strong>{active.messages.toLocaleString('ru-RU')}<small>упоминаний</small></strong><strong>{total ? (active.messages / total * 100).toFixed(1) : 0}%<small>от общего числа</small></strong><strong>{active.causes}<small>причин</small></strong></div>
        {selected && <div className="cluster-selected"><strong>{selected.name}</strong><span>{selected.messages} упоминаний</span><Link to={messageLink(selected)}>Открыть сообщения →</Link></div>}
        <div className="cluster-members">{members.map(point => <div key={point.cause_id} className={selected?.cause_id === point.cause_id ? 'active' : ''}><button onClick={() => setSelected(point)}>{point.name}</button><b>{point.messages}</b><Link to={messageLink(point)} aria-label={`Сообщения: ${point.name}`}>↗</Link></div>)}</div>
      </div>}
    </aside></div>
  </section>;
}
